import { formatNumber } from '@angular/common'
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { getSettingsState } from '@containers/settings/settings.selectors'
import { select, Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { sortCalculationFieldsByIndex } from '@shared/shared.utils'
import { Map } from 'immutable'
import { Subscription } from 'rxjs'

@Component({
  selector: 'property-invoice-item',
  templateUrl: './property-invoice-item.component.html',
  styleUrls: ['./property-invoice-item.component.css'],
})
export class PropertyInvoiceItemComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() public data: Map<string, any>
  @Input() public activeForm: Map<string, any>
  @Input() public propertyDetails: Map<string, any>
  @Output() public onReport: EventEmitter<any> = new EventEmitter()
  @Input() public isPropertyReportGenerating: boolean
  @Input() public dynamicFieldsTranslation: any

  public propertyType: string
  public totalArea: number
  public totalAdjustments: number
  public propertyTax: number
  public taxBeforeDiscount: number
  public assessedAnnualValue: number
  public dynamicFields: any
  private settings$: Subscription
  public settings: any
  public areaSymbol: string
  public currencySymbol: string
  public fields = []

  constructor(public store: Store<any>) {}

  generateFields() {
    const staticFields = [
      {
        translationKey: 'ROOF_LINE_MEASURE',
        labelAddon: this.areaSymbol,
        value: formatNumber(this.roofLineMeasure, 'en', '1.0-4'),
      },
      {
        translationKey: 'NUMBER_OF_FLOORS',
        value: formatNumber(this.numberOfFloors, 'en', '1.0-4'),
      },
      {
        translationKey: 'TOTAL_AREA_MEASURE',
        labelAddon: this.areaSymbol,
        value: formatNumber(this.totalArea, 'en', '1.0-4'),
      },
      {
        translationKey: 'BASE_VALUE',
        value: formatNumber(this.baseValue, 'en', '1.0-4'),
      },
      {
        translationKey: 'TOTAL_ADJUSTMENTS',
        value: `${formatNumber(
          (this.totalAdjustments - 1) * 100,
          'en',
          '1.0-4',
        )}%`,
      },
      {
        translationKey: 'ASSESSED_ANNUAL_VALUE',
        value: formatNumber(this.assessedAnnualValue, 'en', '1.0-4'),
      },
      {
        translationKey: 'MILL_RATE',
        value: `${formatNumber(this.millRate, 'en', '1.0-4')}%`,
      },
      {
        translationKey: 'PROPERTY_TAX_PAYABLE',
        labelAddon: this.currencySymbol,
        value: formatNumber(this.propertyTax, 'en', '1.0-4'),
      },
    ]

    const dynamicFields = Object.keys(this.dynamicFieldsTranslation).map(
      (key) => ({
        translationKey: key,
        index: this.dynamicFieldsTranslation[key]?.index,
        translationDefaultValue: this.dynamicFieldsTranslation[key]?.title,
        translatable: this.dynamicFieldsTranslation[key]?.translatable,
        value:
          !isNaN(parseFloat(this.dynamicFields[key])) &&
          isFinite(this.dynamicFields[key])
            ? formatNumber(parseFloat(this.dynamicFields[key]), 'en', '1.0-4')
            : this.dynamicFields[key],
      }),
    )

    this.fields = sortCalculationFieldsByIndex([
      ...staticFields,
      ...dynamicFields,
    ])
  }

  ngOnInit() {
    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        const settings = state.get('data')

        if (settings.size) {
          this.areaSymbol = settings.getIn([
            'units',
            'SETTINGS_UNITS_AREA_SYMBOL',
          ])
          this.currencySymbol = settings.getIn([
            'units',
            'SETTINGS_UNITS_CURRENCY_SYMBOL',
          ])

          this.generateFields()
        }
      })
  }

  ngOnChanges() {
    const {
      propertyTax,
      propertyType,
      dynamicFields,
      totalAdjustments,
      assessedAnnualValue,
      totalArea,
    } = <any>this.data.toJS()

    this.dynamicFields = dynamicFields
    this.propertyTax = propertyTax
    this.propertyType = propertyType
    this.totalAdjustments = totalAdjustments
    this.totalArea = totalArea
    this.assessedAnnualValue = assessedAnnualValue

    this.generateFields()
  }

  ngOnDestroy(): void {
    this.settings$.unsubscribe()
  }

  get roofLineMeasure(): number {
    return (this.propertyDetails.getIn(['building', 'area']) ?? 0) as number
  }

  get numberOfFloors(): number {
    return this.propertyDetails.getIn([
      'evaluation',
      'evalJson',
      'propertytype',
      this.propertyType,
    ]) as number
  }

  get baseValue(): number {
    const propertyTypeMap: Map<string, any> = this.activeForm.first()

    if (propertyTypeMap) {
      const propertyTypeOption: Map<string, any> = propertyTypeMap
        .get('options')
        .filter((opt: any) => opt.get('label') === this.propertyType)
        .first()

      return parseFloat(propertyTypeOption.get('value'))
    }

    return 0
  }

  get millRate(): number {
    const propertyTypeMap: Map<string, any> = this.activeForm.first()

    if (propertyTypeMap) {
      const propertyTypeOption: Map<string, any> = propertyTypeMap
        .get('options')
        .filter((opt: any) => opt.get('label') === this.propertyType)
        .first()

      return parseFloat(propertyTypeOption.get('rate'))
    }

    return 0
  }
}
